import { useEffect, useState } from "react";
import { usePermissionsStore } from "../permissions/permissions_store";
import moment from "moment";
import DashboardService from "../../services/dashboard.service";
import AccessoryService from "../../services/accessory.service";

import ContactsService from "../../services/contacts.service";
import AlertsService from "../../services/alerts.service";
import TemplatesService from "../../services/templates.service";
import InboxService from "../../services/inbox.service";
import ReportsService from "../../services/reports.service";
import StorageService from "../../services/storage.service";
import SettingsService from "../../services/settings.service";

const rowCount = (key, response) => ({ data: { [key]: Array.isArray(response?.data) ? response.data.length : undefined } });
const validCount = (value) => (typeof value === "number" || (typeof value === "string" && value.trim() !== "")) && Number.isFinite(Number(value)) && Number(value) >= 0;

const METRICS = [
  [["ask_rue_enabled", "ai_trip_planner_enabled"], async () => {
    const response = await SettingsService.get(["guestbook"]);
    const settings = response?.data?.guestbook;
    if (!settings || typeof settings !== "object" || Array.isArray(settings)) return { data: {} };
    // Match Guide Designer's defaults and checkbox state.
    return { data: {
      ask_rue_enabled: Number(Boolean(settings.ask_rue_enabled ?? true)),
      ai_trip_planner_enabled: Number(Boolean(settings.ai_trip_planner_enabled ?? true)),
    } };
  }],
  ["inventory_items", async () => {
    const response = await StorageService.listItems({ page: 0, per: 1 });
    return { data: { inventory_items: response?.data?.total } };
  }],
  ["work_orders_created", () => DashboardService.dashboardCountsWorkOrdersCreated("", "")],
  ["ai_reply_logs", async (start, end) => {
    const response = await ReportsService.getReplyLogs(start.slice(0, 10), end.slice(0, 10), 0, 1, "");
    return { data: { ai_reply_logs: response?.data?.total } };
  }],
  ["chatbot_questions", async (start, end) => {
    const response = await ReportsService.getChatbotLogs(start.slice(0, 10), end.slice(0, 10), 0, 1);
    const data = response?.data;
    // The report returns a null total and an empty page when no logs match.
    const count = data?.total === null && Array.isArray(data.paginated) && data.paginated.length === 0 ? 0 : data?.total;
    return { data: { chatbot_questions: count } };
  }],
  ["smart_lock_events", async (start, end) => {
    const response = await ReportsService.getSmartLockLogs(start.slice(0, 10), end.slice(0, 10), 0, 1, "");
    return { data: { smart_lock_events: response?.data?.total } };
  }],
  ["guest_ratings", async (start, end) => {
    const response = await ReportsService.getFiveStar(start.slice(0, 10), end.slice(0, 10), 0, 1, "");
    return { data: { guest_ratings: response?.data?.total } };
  }],
  ["contacts", async () => rowCount("contacts", await ContactsService.getContacts())],
  ["alerts", async () => rowCount("alerts", await AlertsService.getAlerts())],
  ["saved_responses", async () => {
    const response = await TemplatesService.get(["saved_responses"]);
    return rowCount("saved_responses", { data: response?.data?.saved_responses });
  }],
  ["inbox_conversations", async () => {
    // Include archived conversations, without counting empty threads from the All view.
    const responses = await Promise.all([InboxService.getInboxes(0, "", 1), InboxService.getInboxes(0, "archived", 1)]);
    const counts = responses.map((response) => response?.data?.count);
    return { data: { inbox_conversations: counts.every(validCount) ? counts.reduce((sum, count) => sum + Number(count), 0) : undefined } };
  }],
  ["guest_guide_views", (start, end) => DashboardService.dashboardCountsGuestGuideViews(start, end)],
  ["survey_responses", (start, end) => DashboardService.dashboardCountsSurveyResponses(start, end)],
  ["extend_stays_messages_sent", (start, end) => DashboardService.dashboardCountsExtendStaysMessagesSent(start, end)],
  ["area_guides", () => DashboardService.dashboardCountsAreaGuides()],
  ["scheduled_messages", () => DashboardService.dashboardCountsScheduledMessages()],
  ["extras", async () => {
    const response = await AccessoryService.retrieve();
    return { data: { extras: Array.isArray(response?.data) ? response.data.length : undefined } };
  }],
];

export const getUsage = (feature, metrics = {}) => {
  const count = metrics[feature.metric];
  if (typeof count !== "number" || !Number.isFinite(count) || count < 0) {
    return { state: "unknown", label: "Not tracked yet", detail: "Usage information is not available for this feature." };
  }
  if (feature.usageBasis === "setting") {
    return count > 0
      ? { state: "active", label: "Turned on", detail: "Enabled in Guide Designer’s AI & Trip Planner settings." }
      : { state: "inactive", label: "Turned off", detail: "Enable this in Guide Designer’s AI & Trip Planner settings." };
  }
  if (feature.usageBasis === "current") {
    return count > 0
      ? { state: "active", label: "Your team uses this", detail: `${count.toLocaleString()} ${count === 1 ? feature.metricSingular : feature.metricLabel} in your account.` }
      : { state: "inactive", label: `No ${feature.metricLabel} yet`, detail: feature.emptyUsageDetail };
  }
  if (count > 0) {
    return { state: "active", label: "Your team uses this", detail: `${count.toLocaleString()} ${feature.metricLabel} in the last 12 months.` };
  }
  return { state: "inactive", label: "No recent activity", detail: `No ${feature.metricLabel} recorded in the last 12 months. This does not mean your team has never used it.` };
};

const METRIC_MODULES = {
  ask_rue_enabled: ['guides.master', 'guides.home'], inventory_items: ['operations.inventory'],
  work_orders_created: ['dashboard'], ai_reply_logs: ['reports'], chatbot_questions: ['reports'],
  smart_lock_events: ['reports'], guest_ratings: ['reports'], contacts: ['messaging.contacts', 'crm.contacts'],
  alerts: ['messaging.alerts'], saved_responses: ['messaging.responses'], inbox_conversations: ['messaging.messages'],
  guest_guide_views: ['dashboard'], survey_responses: ['dashboard'], extend_stays_messages_sent: ['dashboard'],
  area_guides: ['dashboard'], scheduled_messages: ['dashboard'], extras: ['guides.extras'],
};

export const useFeatureUsage = (accountId) => {
  const { canView, permsLoaded } = usePermissionsStore();
  const [result, setResult] = useState({ accountId: null, metrics: {}, loading: true, failed: false });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!accountId || !permsLoaded) return;
    const allowedMetrics = METRICS.filter(([key]) => METRIC_MODULES[[key].flat()[0]]?.some(canView));
    let active = true;
    const now = moment();
    const end = now.format("YYYY-MM-DD HH:mm:ss");
    const start = now.clone().subtract(12, "months").format("YYYY-MM-DD HH:mm:ss");
    setResult({ accountId, metrics: {}, loading: true, failed: false });
    Promise.allSettled(allowedMetrics.map(([, load]) => load(start, end))).then((responses) => {
      if (!active) return;
      const metrics = {};
      let failed = false;
      responses.forEach((response, index) => {
        const keys = [allowedMetrics[index][0]].flat();
        keys.forEach((key) => {
          const raw = response.status === "fulfilled" ? response.value?.data?.[key] : undefined;
          if (validCount(raw)) metrics[key] = Number(raw);
          else failed = true;
        });
      });
      setResult({ accountId, metrics, loading: false, failed });
    });
    return () => { active = false; };
  }, [accountId, attempt, canView, permsLoaded]);
  return {
    ...(result.accountId === accountId ? result : { metrics: {}, loading: !!accountId, failed: false }),
    retry: () => setAttempt((value) => value + 1),
  };
};
